import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WifiHigh, Lightning, GasPump, ForkKnife, ShoppingCart, Bus, Coffee, House, TrendUp, TrendDown } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useRate } from "../context/RateContext";
import { useT } from "../i18n";
import { formatMAD } from "../lib/format";
import CurrencyConverter from "../components/CurrencyConverter";
import api from "../lib/api";

const CostOfLiving = () => {
  const rate = useRate();
  const { t } = useT();
  const [history, setHistory] = useState([]);
  const toMad = (eur) => eur * (rate.eur_to_mad || 10.7);

  useEffect(() => {
    document.title = "Coût de la vie & change | YADIMMO";
    api.get("/exchange-rate/history").then(({ data }) => setHistory(data.series || [])).catch(() => {});
  }, []);

  const ITEMS = [
    { icon: House, key: "rent", ma: 350, fr: 780, unit: t("col.per_month"), hint: "70 m² · centre-ville" },
    { icon: WifiHigh, key: "internet", ma: 25, fr: 35, unit: t("col.per_month"), hint: "Fibre 100+ Mbps illimité" },
    { icon: Lightning, key: "electricity", ma: 45, fr: 115, unit: t("col.per_month"), hint: "≈ 200 kWh/mois · logement 70 m²" },
    { icon: GasPump, key: "fuel", ma: 1.30, fr: 1.85, unit: t("col.per_liter"), hint: "Essence sans plomb · 1 litre" },
    { icon: ForkKnife, key: "restaurant", ma: 8, fr: 18, unit: "", hint: "Plat principal · restaurant moyen" },
    { icon: ShoppingCart, key: "groceries", ma: 45, fr: 80, unit: t("col.per_week"), hint: "Panier hebdo · 1 personne" },
    { icon: Bus, key: "transport", ma: 0.40, fr: 1.90, unit: "", hint: "1 trajet urbain (bus/tram)" },
    { icon: Coffee, key: "coffee", ma: 1.20, fr: 2.80, unit: "", hint: "Expresso · café standard" },
  ];

  const rates = history.map((h) => h.rate);
  const trend = rates.length > 1 ? rates[rates.length - 1] - rates[0] : 0;
  const last = rates.length ? rates[rates.length - 1] : rate.eur_to_mad;

  return (
    <div className="relative z-10 pt-[72px] min-h-screen bg-background">
      <div className="bg-navy text-white py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight">{t("col.title")}</h1>
          <p className="text-white/60 mt-4 max-w-2xl">{t("col.subtitle")}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 space-y-12">
        {/* Chart */}
        <div className="bg-white border border-navy/10 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="text-sm text-muted-foreground">EUR / MAD · 90 jours</div>
              <div className="flex items-baseline gap-3">
                <span className="font-roboto font-black text-3xl text-navy">{last?.toFixed?.(3)}</span>
                <span className={`inline-flex items-center gap-1 text-sm font-roboto font-bold ${trend >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {trend >= 0 ? <TrendUp size={16} /> : <TrendDown size={16} />} {trend >= 0 ? "+" : ""}{trend.toFixed(3)}
                </span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">1 € = {rate.eur_to_mad?.toFixed?.(3)} MAD</div>
          </div>
          <div className="h-[260px] w-full" data-testid="rate-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history} margin={{ left: -12, right: 8, top: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E0A85A" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#E0A85A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} tickFormatter={(d) => d.slice(5)} minTickGap={40} />
                <YAxis domain={["dataMin - 0.1", "dataMax + 0.1"]} tickFormatter={(v) => Number(v).toFixed(2)} tick={{ fontSize: 11, fill: "#9ca3af" }} width={48} />
                <Tooltip formatter={(v) => [`${v} MAD`, "1 €"]} labelStyle={{ color: "#1A3A5C" }} />
                <Area type="monotone" dataKey="rate" stroke="#1A3A5C" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <CurrencyConverter />

        {/* Comparison table */}
        <div>
          <h2 className="font-display text-2xl sm:text-3xl text-navy font-light mb-6">{t("col.compare")}</h2>
          <div className="overflow-x-auto border border-navy/10 rounded-2xl bg-white">
            <table className="w-full text-sm" data-testid="cost-table">
              <thead>
                <tr className="bg-navy text-white">
                  <th className="text-start font-medium px-5 py-4">{t("col.item")}</th>
                  <th className="text-end font-medium px-5 py-4">🇲🇦 {t("col.morocco")}</th>
                  <th className="text-end font-medium px-5 py-4">🇫🇷 {t("col.france")}</th>
                  <th className="text-end font-medium px-5 py-4">{t("col.gap")}</th>
                </tr>
              </thead>
              <tbody>
                {ITEMS.map((it) => {
                  const save = Math.round((1 - it.ma / it.fr) * 100);
                  return (
                    <tr key={it.key} className="border-t border-navy/5 hover:bg-background transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3 text-navy font-medium"><it.icon size={20} weight="light" className="text-gold" /> {t(`col.${it.key}`)} <span className="text-xs text-muted-foreground">{it.unit}</span></div>
                        <div className="text-xs text-muted-foreground mt-1 ms-8">{it.hint}</div>
                      </td>
                      <td className="px-5 py-4 text-end">
                        <div className="font-roboto font-bold text-navy text-base">{it.ma.toLocaleString("fr-FR")} €</div>
                        <div className="text-xs font-roboto font-bold text-gold">{formatMAD(toMad(it.ma))} MAD</div>
                      </td>
                      <td className="px-5 py-4 text-end font-roboto font-bold text-navy text-base">{it.fr.toLocaleString("fr-FR")} €</td>
                      <td className="px-5 py-4 text-end">
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-roboto font-black bg-green-100 text-green-700">
                          <TrendDown size={15} weight="bold" /> -{save}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">{t("col.note")}</p>
        </div>

        <div className="bg-navy text-white rounded-2xl p-8 text-center">
          <h3 className="font-display text-2xl mb-3">{t("col.cta_title")}</h3>
          <Link to="/biens" className="inline-block bg-gold text-navy font-semibold px-7 py-3.5 rounded-full hover:bg-[#C8924A] transition-colors">{t("col.cta_btn")}</Link>
        </div>
      </div>
    </div>
  );
};

export default CostOfLiving;
